import { ValidationUtils } from '@victory-lib-ui/shared/utils';

type ApiFailurePayload = {
  message?: string;
  errors?: Record<string, string[]>;
};

type HttpFailure = {
  status?: number;
  error?: ApiFailurePayload;
  message?: string;
};

type AuthFailureContext =
  | 'login'
  | 'register'
  | 'request_password_reset'
  | 'verify_password_reset'
  | 'reset_password';

type PasswordRequirementScope = 'register' | 'reset';

export function flattenValidationErrors(
  validationErrors: Record<string, string[]> | undefined
): Record<string, string> {
  if (!validationErrors) {
    return {};
  }

  const flattenedErrors: Record<string, string> = {};

  Object.keys(validationErrors).forEach((fieldName) => {
    const fieldMessages = validationErrors[fieldName];
    if (Array.isArray(fieldMessages) && fieldMessages.length > 0) {
      flattenedErrors[fieldName] = fieldMessages[0];
    }
  });

  return flattenedErrors;
}

export function extractValidationErrors(
  failure: Error | HttpFailure
): Record<string, string> {
  if (!isHttpFailure(failure)) {
    return {};
  }

  return flattenValidationErrors(failure.error?.errors);
}

export function resolveAuthFailureMessageKey(
  context: AuthFailureContext,
  failure: Error | HttpFailure
): string {
  const failureStatus = isHttpFailure(failure) ? failure.status ?? null : null;

  if (context === 'login') {
    if (failureStatus === 401) {
      return 'login.error_invalid_credentials';
    }

    if (failureStatus === 0 || failureStatus === 500) {
      return 'login.error_unavailable';
    }

    return 'login.error_unexpected';
  }

  if (context === 'register') {
    if (failureStatus === 409) {
      return 'register.error_email_in_use';
    }

    if (failureStatus === 422) {
      return 'register.error_validation';
    }

    if (failureStatus === 0 || failureStatus === 500) {
      return 'register.error_unavailable';
    }

    return 'register.error_unexpected';
  }

  if (context === 'request_password_reset') {
    if (failureStatus === 422) {
      return 'reset.request_error_validation';
    }

    if (failureStatus === 0 || failureStatus === 500) {
      return 'reset.request_error_unavailable';
    }

    return 'reset.request_error_unexpected';
  }

  if (context === 'verify_password_reset') {
    if (failureStatus === 400) {
      return 'reset.verify_error_invalid';
    }

    if (failureStatus === 0 || failureStatus === 500) {
      return 'reset.verify_error_unavailable';
    }

    return 'reset.verify_error_unexpected';
  }

  if (failureStatus === 400) {
    return 'reset.reset_error_validation';
  }

  if (failureStatus === 0 || failureStatus === 500) {
    return 'reset.reset_error_unavailable';
  }

  return 'reset.reset_error_unexpected';
}

export function resolvePasswordRequirementMessageKey(
  scope: PasswordRequirementScope,
  password: string
): string {
  const passwordRequirements = ValidationUtils.getPasswordRequirements(password);
  const translationPrefix =
    scope === 'register'
      ? 'register.password_requirements'
      : 'reset.requirements';

  if (!passwordRequirements.hasMinLength) {
    return `${translationPrefix}.min`;
  }

  if (!passwordRequirements.hasUppercase) {
    return `${translationPrefix}.upper`;
  }

  if (!passwordRequirements.hasLowercase) {
    return `${translationPrefix}.lower`;
  }

  if (!passwordRequirements.hasNumber) {
    return `${translationPrefix}.number`;
  }

  return `${translationPrefix}.special`;
}

function isHttpFailure(failure: Error | HttpFailure): failure is HttpFailure {
  return typeof failure === 'object' && failure !== null && 'status' in failure;
}
